import { ChangeDetectionStrategy, Component, Injector } from '@angular/core';
import { forkJoin } from 'rxjs';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { ColumnVisibility } from '../../../core/model/column-definition';
import { Command, CommandBase, FilteredCommand } from '../../../core/model/command';
import { FilterDefinitionItem, FilterDefinitionItemLocalDate } from '../../../core/model/filter-definition-item';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { StandardCommandHandlerResolver } from '../../../core/service/command/standard-command-handler-resolver.service';
import { AllowSort } from '../../../core/service/factory/allow-sort';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { CompanyService } from '../../../rent/service/data/company.service';
import { AmortizationPlanPaymentNoticeListItem } from '../../model/amortization-plan-payment-notice-list-item';
import { GetReminderDeliveryMethodIcon, PaymentNoticeDeliveryStatus, ReminderDeliveryMethod, ReminderDeliveryMethodToString } from '../../model/const/enum';
import { AmortizationPlanPaymentNoticeService } from '../../service/data/amortization-plan-payment-notice.service';
import { RegisterPaymentNoticePaymentFormComponent } from '../register-payment-notice-payment-form/register-payment-notice-payment-form.component';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';

const paymentNoticeListCommand = {
  registerPayment: 'registerAmortizationPlanPaymentNoticePayment',
  requestDelivery: 'requestAmortizationPlanPaymentNoticeDelivery',
};

enum PaymentNoticePaymentStatus {
  All = 0,
  NotFullyPaid = 1,
  FullyPaid = 2,
}

enum PaymentNoticeDeliveryFilterStatus {
  All = 0,
  NotSent = 1,
  QueuedOrSent = 2,
}

@Component({
  standalone: false,
  selector: 'app-amortization-plan-payment-notice-manager',
  templateUrl: './../../../core/component/paged-object-manager/paged-object-manager.html',
  styleUrls: ['./../../../core/component/paged-object-manager/paged-object-manager.scss'],
  providers: [AmortizationPlanPaymentNoticeService],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AmortizationPlanPaymentNoticeManagerComponent extends PagedObjectManagerComponent<AmortizationPlanPaymentNoticeListItem> {
  private canManagePaymentNotices = false;

  constructor(
    private readonly _dataService: AmortizationPlanPaymentNoticeService,
    private readonly _companyService: CompanyService,
    private readonly _dialogService: ActionWindowService,
    private readonly _injector: Injector,
    accountService: UserAccountService,
    columnFactory: ColumnDefinitionFactory,
    dispatcher: CommandDispatcher,
    handlerResolver: StandardCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService
  ) {
    super(_dataService, messageService, navigationService, dispatcher, handlerResolver);
    this.columnDefinition = [
      columnFactory.createNumber('AMORTIZATION_PLAN.PAYMENT_NOTICE.SEQUENCE_NUMBER', 'sequenceNumber', { serverSideSortBinding: 'sequenceNumber' }),
      columnFactory.create('INVOICE.OCRNUMBER', 'ocrNumber', { serverSideSortBinding: 'ocrNumber' }),
      columnFactory.createNumber('AMORTIZATION_PLAN.NUMBER', 'amortizationPlanNumber', { allowSort: AllowSort.No }),
      columnFactory.create('COMPANY.LIST_HEADER', 'companyName', { allowSort: AllowSort.No }),
      columnFactory.create('COMMON.CONTACT', 'debtorContactName', { allowSort: AllowSort.No }),
      columnFactory.createDate('INVOICE.DUE_DATE', 'dueDate', { serverSideSortBinding: 'dueDate' }),
      columnFactory.createMoney('COMMON.AMOUNT', 'amount', { serverSideSortBinding: 'amount' }),
      columnFactory.createMoney('INVOICE.REMAINING_AMOUNT', 'remainingAmount', { visibility: ColumnVisibility.default, allowSort: AllowSort.No }),
      columnFactory.createTranslation('AMORTIZATION_PLAN.PAYMENT_NOTICE.DELIVERY_METHOD', 'deliveryMethodCaption', {
        serverSideSortBinding: 'deliveryMethod',
      }),
      columnFactory.createTranslation('AMORTIZATION_PLAN.PAYMENT_NOTICE.LATEST_DELIVERY', 'latestDeliveryStatusCaption', {
        visibility: ColumnVisibility.default,
        allowSort: AllowSort.No,
      }),
      columnFactory.createDateTime('AMORTIZATION_PLAN.PAYMENT_NOTICE.SENT_AT', 'sentDate', {
        visibility: ColumnVisibility.default,
        serverSideSortBinding: 'sentDate',
      }),
      columnFactory.createDateTime('COMMON.CREATED_DATE', 'createdDate', { visibility: ColumnVisibility.optional, serverSideSortBinding: 'createdDate' }),
      columnFactory.createTruncated<AmortizationPlanPaymentNoticeListItem>('AMORTIZATION_PLAN.PAYMENT_NOTICE.DELIVERY_ADDRESS', 'deliveryAddress', {
        visibility: ColumnVisibility.optional,
        maxLength: 45,
        helpBuilder: paymentNotice => paymentNotice.deliveryAddress,
      }),
      columnFactory.createNumber('AMORTIZATION_PLAN.PAYMENT_NOTICE.ATTEMPT_COUNT', 'latestDeliveryAttemptCount', {
        visibility: ColumnVisibility.optional,
        allowSort: AllowSort.No,
      }),
    ];
    this.title = 'AMORTIZATION_PLAN.PAYMENT_NOTICE.LIST_HEADER';
    this.allowNew = false;
    this.allowOpenOnRowClick = true;
    this.preferencesKey = 'invoice.amortization-plan-payment-notice.list';
    this.AddSubscription(
      accountService.current.subscribe(account => {
        this.canManagePaymentNotices = account.hasAccess(ModuleNames.Invoicing, Features.ManageInvoices);
        this.allowNew = this.canManagePaymentNotices;

        if (this.canManagePaymentNotices) {
          this.addMenuItemDefinition(
            'AMORTIZATION_PLAN.PAYMENT_NOTICE.SEND_ACTION',
            paymentNoticeListCommand.requestDelivery
          );
          this.addContextMenuItemDefinition(
            'AMORTIZATION_PLAN.PAYMENT_NOTICE.SEND_ACTION',
            paymentNoticeListCommand.requestDelivery,
            context => !this.canRequestPaymentNoticeDelivery(context)
          );
          this.addContextMenuItemDefinition(
            'AMORTIZATION_PLAN.PAYMENT_NOTICE.REGISTER_PAYMENT_ACTION',
            paymentNoticeListCommand.registerPayment,
            context => !this.canRegisterPaymentNoticePayment(context)
          );
        }
      })
    );
  }

  override canExecute(command: CommandBase): boolean {
    if (!this.canManagePaymentNotices && (
      command.id === paymentNoticeListCommand.registerPayment ||
      command.id === paymentNoticeListCommand.requestDelivery
    )) {
      return false;
    }

    if (command.id === paymentNoticeListCommand.registerPayment) {
      return command instanceof Command && this.canRegisterPaymentNoticePayment(command.context);
    }

    if (command.id === paymentNoticeListCommand.requestDelivery) {
      if (command instanceof FilteredCommand) {
        command.filterProvider = this;
        return command.totalAffectedItemsControlValue > 0;
      }

      return command instanceof Command && this.canRequestPaymentNoticeDelivery(command.context);
    }

    return super.canExecute(command);
  }

  override execute(command: CommandBase): void {
    if (command.id === paymentNoticeListCommand.registerPayment) {
      if (command instanceof Command) {
        this.registerPaymentNoticePayment(command.context);
      }

      return;
    }

    if (command.id === paymentNoticeListCommand.requestDelivery) {
      if (command instanceof FilteredCommand) {
        this.requestFilteredPaymentNoticeDelivery(command);
      } else if (command instanceof Command) {
        this.requestPaymentNoticeDelivery(command.context);
      }

      return;
    }

    super.execute(command);
  }

  override onRowClicked(e: { context: AmortizationPlanPaymentNoticeListItem; event: PointerEvent }) {
    if (!e?.context?.amortizationPlanId) {
      return;
    }

    this.navigation.navigate(
      ['amortization-plan-payment-notices', 'edit', e.context.amortizationPlanId],
      null,
      e.event?.ctrlKey || e.event?.metaKey
    );
  }

  override onCreateItem(): void {
    if (!this.canManagePaymentNotices) {
      return;
    }

    this.navigation.navigate(['amortization-plan-payment-notices', 'create']);
  }

  ngOnInit() {
    this.AddSubscription(
      forkJoin({
        definition: this._dataService.getFilterDefinition(),
      }).subscribe({
        next: ({ definition }) => {
          this.defaultFilters = definition.filter.defaultFields;
          this.pageSize = definition.pageSize || this.pageSize;

          this.filterDefinition = [
            new FilterDefinitionItem(
              'COMPANY.LIST_HEADER',
              'companyId',
              definition.filter.companyId,
              this._companyService,
              definition.filter.selectedFields.includes('companyId')
            ),
            new FilterDefinitionItem(
              'AMORTIZATION_PLAN.NUMBER',
              'amortizationPlanNumber',
              definition.filter.amortizationPlanNumber,
              null,
              definition.filter.selectedFields.includes('amortizationPlanNumber')
            ),
            new FilterDefinitionItem(
              'INVOICE.OCRNUMBER',
              'ocrNumber',
              definition.filter.ocrNumber,
              null,
              definition.filter.selectedFields.includes('ocrNumber')
            ),
            new FilterDefinitionItem(
              'AMORTIZATION_PLAN.PAYMENT_NOTICE.DELIVERY_METHOD',
              'deliveryMethod',
              definition.filter.deliveryMethod,
              [
                { caption: 'ALL_TEXT', value: null },
                { caption: ReminderDeliveryMethodToString(ReminderDeliveryMethod.Email), value: ReminderDeliveryMethod.Email, icon: GetReminderDeliveryMethodIcon(ReminderDeliveryMethod.Email) },
                { caption: ReminderDeliveryMethodToString(ReminderDeliveryMethod.Mail), value: ReminderDeliveryMethod.Mail, icon: GetReminderDeliveryMethodIcon(ReminderDeliveryMethod.Mail) },
                { caption: ReminderDeliveryMethodToString(ReminderDeliveryMethod.Manual), value: ReminderDeliveryMethod.Manual, icon: GetReminderDeliveryMethodIcon(ReminderDeliveryMethod.Manual) },
              ],
              definition.filter.selectedFields.includes('deliveryMethod')
            ),
            new FilterDefinitionItem(
              'AMORTIZATION_PLAN.PAYMENT_NOTICE.PAYMENT_STATUS',
              'paymentStatus',
              definition.filter.paymentStatus,
              [
                { caption: 'ALL_TEXT', value: PaymentNoticePaymentStatus.All },
                { caption: 'AMORTIZATION_PLAN.PAYMENT_NOTICE.PAYMENT_STATUS_NOT_FULLY_PAID', value: PaymentNoticePaymentStatus.NotFullyPaid },
                { caption: 'AMORTIZATION_PLAN.PAYMENT_NOTICE.PAYMENT_STATUS_FULLY_PAID', value: PaymentNoticePaymentStatus.FullyPaid },
              ],
              definition.filter.selectedFields.includes('paymentStatus')
            ),
            new FilterDefinitionItem(
              'AMORTIZATION_PLAN.PAYMENT_NOTICE.DELIVERY_STATE',
              'deliveryStatus',
              definition.filter.deliveryStatus,
              [
                { caption: 'ALL_TEXT', value: PaymentNoticeDeliveryFilterStatus.All },
                { caption: 'AMORTIZATION_PLAN.PAYMENT_NOTICE.DELIVERY_STATE_NOT_SENT', value: PaymentNoticeDeliveryFilterStatus.NotSent },
                { caption: 'AMORTIZATION_PLAN.PAYMENT_NOTICE.DELIVERY_STATE_QUEUED_OR_SENT', value: PaymentNoticeDeliveryFilterStatus.QueuedOrSent },
              ],
              definition.filter.selectedFields.includes('deliveryStatus')
            ),
            new FilterDefinitionItemLocalDate(
              'AMORTIZATION_PLAN.PAYMENT_NOTICE.DUE_FROM_DATE',
              'dueFrom',
              definition.filter.dueFrom,
              definition.filter.selectedFields.includes('dueFrom')
            ),
            new FilterDefinitionItemLocalDate(
              'AMORTIZATION_PLAN.PAYMENT_NOTICE.DUE_TO_DATE',
              'dueTo',
              definition.filter.dueTo,
              definition.filter.selectedFields.includes('dueTo')
            ),
            new FilterDefinitionItemLocalDate(
              'COMMON.CREATED_DATE_FROM',
              'createdFrom',
              definition.filter.createdFrom,
              definition.filter.selectedFields.includes('createdFrom')
            ),
            new FilterDefinitionItemLocalDate(
              'COMMON.CREATED_DATE_TO',
              'createdTo',
              definition.filter.createdTo,
              definition.filter.selectedFields.includes('createdTo')
            ),
          ];
        },
        error: error => {
          this.messageService.showError(error);
        },
      })
    );
  }

  private canRegisterPaymentNoticePayment(paymentNotice: AmortizationPlanPaymentNoticeListItem): boolean {
    return this.canManagePaymentNotices && !!paymentNotice?.id && !!paymentNotice?.remainingAmount?.IsPositive;
  }

  private canRequestPaymentNoticeDelivery(paymentNotice: AmortizationPlanPaymentNoticeListItem): boolean {
    if (!this.canManagePaymentNotices || !paymentNotice?.id || !this.isSendableReminderMethod(paymentNotice.deliveryMethod)) {
      return false;
    }

    const status = paymentNotice.latestDeliveryStatus;
    return status === undefined || status === null || status === PaymentNoticeDeliveryStatus.Failed;
  }

  private requestPaymentNoticeDelivery(paymentNotice: AmortizationPlanPaymentNoticeListItem): void {
    if (!this.canRequestPaymentNoticeDelivery(paymentNotice)) {
      return;
    }

    const confirmKey = typeof paymentNotice.latestDeliveryStatus === 'number'
      ? 'AMORTIZATION_PLAN.PAYMENT_NOTICE.RESEND_CONFIRM'
      : 'AMORTIZATION_PLAN.PAYMENT_NOTICE.SEND_CONFIRM';

    this.AddSubscription(
      this.messageService.showDialog(confirmKey).subscribe(confirmed => {
        if (!confirmed) {
          return;
        }

        this.AddSubscription(
          this._dataService
            .requestDeliveryFiltered(this.getCurrentFilterForAction(), [paymentNotice.id], [], 1)
            .subscribe({
              next: () => this.reloadCurrentList(),
              error: error => this.messageService.showError(error),
            })
        );
      })
    );
  }

  private requestFilteredPaymentNoticeDelivery(command: FilteredCommand): void {
    this.AddSubscription(
      this.messageService.showDialog('AMORTIZATION_PLAN.PAYMENT_NOTICE.SEND_SELECTED_CONFIRM').subscribe(confirmed => {
        if (!confirmed) {
          return;
        }

        this.AddSubscription(
          this._dataService
            .requestDeliveryFiltered(
              command.filterProvider.filterState,
              command.includedIds,
              command.excludedIds,
              command.totalAffectedItemsControlValue
            )
            .subscribe({
              next: () => this.reloadCurrentList(),
              error: error => this.messageService.showError(error),
            })
        );
      })
    );
  }

  private registerPaymentNoticePayment(paymentNotice: AmortizationPlanPaymentNoticeListItem): void {
    if (!this.canRegisterPaymentNoticePayment(paymentNotice)) {
      return;
    }

    this.AddSubscription(
      this._dialogService.open(RegisterPaymentNoticePaymentFormComponent, this._injector, null, paymentNotice, false).subscribe(result => {
        if (result) {
          this.reloadCurrentList();
        }
      })
    );
  }

  private reloadCurrentList(): void {
    const filter = this.getCurrentFilterForAction();

    (this as any).sendPageFilter(filter);
  }

  private getCurrentFilterForAction(): any {
    const selectedFields = this.filterComponent?.filters?.filter(filter => filter.selected).map(filter => filter.name) ?? this.filterState?.selectedFields;
    return {
      ...(this.filterComponent?.form?.value ?? this.filterState ?? this.getPagingChangedFallbackFilter()),
      selectedFields: selectedFields ?? this.defaultFilters ?? [],
    };
  }

  private isSendableReminderMethod(method: ReminderDeliveryMethod | null | undefined): boolean {
    return method === ReminderDeliveryMethod.Email
      || method === ReminderDeliveryMethod.Mail
      || method === ReminderDeliveryMethod.Manual;
  }
}
